import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { getCourseBundle, getCurriculum } from "../services/courses";
import { getMyPurchases } from "../services/purchase";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import {
  Play, CheckCircle2, ChevronLeft, ChevronRight, ChevronDown, Info, MessageSquare, ListVideo, ArrowLeft, FileText, Sparkles, Brain, RefreshCcw, AlertCircle, Video, ClipboardCheck, Library, BookOpen
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';

export default function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // Start with overview
  const [isSecure, setIsSecure] = useState(true);

  useEffect(() => {
    const handleBlur = () => setIsSecure(false);
    const handleFocus = () => setIsSecure(true);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);
  const [expandedModules, setExpandedModules] = useState({});
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPageCount, setPdfPageCount] = useState(null);
  const [completedLessonIds, setCompletedLessonIds] = useState([]);
  const [isMarking, setIsMarking] = useState(false);
  const [aiNotes, setAiNotes] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [flipFlash, setFlipFlash] = useState(false);
  const [currentFlashIndex, setCurrentFlashIndex] = useState(0);
  const [isPurchased, setIsPurchased] = useState(false);

  useEffect(() => {
    getMyPurchases().then(res => {
      const purchases = res.data;
      const purchased = purchases.some(p => String(p.id) === String(courseId));
      setIsPurchased(purchased);
    }).catch(err => console.error(err));
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;

    // Fetch Course Bundle
    getCourseBundle(courseId).then(res => setBundle(res.data)).catch(() => toast.error("Failed to load course bundle"));

    // Fetch Curriculum
    getCurriculum(courseId).then((res) => {
      const data = res.data || [];
      setCurriculum(data);
      if (data.length > 0) {
        setExpandedModules({ [data[0].id]: true });
        if (data[0].lessons && data[0].lessons.length > 0) {
          setCurrentVideo(data[0].lessons[0]);
        }
      }
    }).catch(err => {
      toast.error("Failed to load curriculum");
    });

    api.get(`/student/lesson-progress/${courseId}`).then(res => {
      setCompletedLessonIds(res.data || []);
    }).catch(() => { });
  }, [courseId]);

  const allLessons = curriculum.flatMap(m => m.lessons || []);

  useEffect(() => {
    setPdfPage(1);
    setPdfPageCount(null);
    setAiNotes(null);
  }, [currentVideo?.id]);

  useEffect(() => {
    if (activeTab === "ai-notes" && currentVideo && !aiNotes) {
      setLoadingAi(true);
      api.get(`/ai/notes/${currentVideo.id}`)
        .then(res => {
          setAiNotes(res.data);
          setCurrentFlashIndex(0);
          setFlipFlash(false);
        })
        .catch(err => toast.error("AI currently overloaded."))
        .finally(() => setLoadingAi(false));
    }
  }, [activeTab, currentVideo, aiNotes]);

  const handleMarkComplete = async () => {
    if (!currentVideo || isMarking) return;
    const isAlreadyDone = completedLessonIds.includes(currentVideo.id);
    setIsMarking(true);
    try {
      await api.post(`/student/lesson-progress/${currentVideo.id}?completed=${!isAlreadyDone}`);
      if (isAlreadyDone) {
        setCompletedLessonIds(prev => prev.filter(id => id !== currentVideo.id));
        toast.success("Marked incomplete");
      } else {
        setCompletedLessonIds(prev => [...prev, currentVideo.id]);
        toast.success("Lesson complete! ✨");
      }
    } catch (err) {
      toast.error("Failed to update progress");
    } finally {
      setIsMarking(false);
    }
  };

  const handleEnded = () => {
    const index = allLessons.findIndex((l) => l.id === currentVideo?.id);
    const next = allLessons[index + 1];
    if (next) {
      setCurrentVideo(next);
      toast.success(`Next: ${next.title}`);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  if (!bundle) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950 font-['Inter',sans-serif] overflow-hidden">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-6 py-4 flex items-center gap-4 shrink-0">
        <button onClick={() => navigate("/dashboard?tab=my-courses")} className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mb-1">Course Hub</div>
          <h1 className="text-xl font-black text-slate-900 dark:text-white truncate max-w-2xl">{bundle?.title || "Complete Course"}</h1>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Column: Video Player & Tabs */}
        <div className="flex-1 flex flex-col overflow-y-auto bg-white dark:bg-slate-900">
          
          {/* Video Player Section */}
          <div className="w-full bg-black aspect-video relative flex flex-col justify-center shrink-0 select-none overflow-hidden" onContextMenu={(e) => e.preventDefault()}>
            {!isSecure && (
              <div className="absolute inset-0 bg-black z-[60] flex flex-col items-center justify-center text-white p-10 text-center">
                <div className="bg-red-600/20 p-4 rounded-full mb-4">
                  <Info size={40} className="text-red-500" />
                </div>
                <h2 className="text-2xl font-black mb-2">SECURITY ALERT</h2>
                <p className="text-slate-400 max-w-md">Content is protected. Recording or switching windows is restricted for security reasons. Please focus back to continue.</p>
              </div>
            )}
            
            {currentVideo ? (
              <>
                {/* Dynamic Watermark */}
                <div className="absolute top-1/4 left-1/4 opacity-10 text-white text-xs font-bold pointer-events-none select-none z-50 animate-pulse">
                  Antigravity User: {localStorage.getItem("token") ? "Protected Access" : "Guest"}
                </div>
                
                {currentVideo.type === "pdf" ? (
                  <iframe src={`${currentVideo.content_url}#toolbar=0`} className="w-full h-full bg-white" title="PDF Viewer" onContextMenu={(e) => e.preventDefault()} />
                ) : (
                  <video src={currentVideo.content_url} className="w-full h-full object-contain" controls autoPlay onEnded={handleEnded} controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} />
                )}
              </>
            ) : (
              <div className="text-center text-slate-500">
                <Play size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a lesson from curriculum</p>
              </div>
            )}
          </div>

          {/* Navigation Tabs (Below Video) */}
          <div className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-6 overflow-x-auto no-scrollbar shrink-0">
            <div className="flex gap-8 min-w-max">
              {[
                { id: "overview", label: "Overview", icon: Info },
                { id: "live", label: "Live Classes", icon: Video },
                { id: "exams", label: "Mock Tests", icon: ClipboardCheck },
                { id: "assignments", label: "Assignments", icon: FileText },
                { id: "library", label: "Digital Library", icon: Library },
                { id: "doubts", label: "Doubt Desk", icon: MessageSquare },
                { id: "ai-notes", label: "AI Tutor", icon: Brain, isPremium: true },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                    }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                  {tab.isPremium && <span className="ml-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-black">AI</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content Area */}
          <div className="p-6 lg:p-10 w-full pb-32">
            {activeTab === "overview" && (
              <div className="space-y-8 animate-fade-in">
                <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Welcome to your Course Hub</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <div className="text-slate-500 text-sm font-bold mb-2">Recorded Lessons</div>
                    <div className="text-3xl font-black text-indigo-600">{bundle?.lessons?.length || 0}</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <div className="text-slate-500 text-sm font-bold mb-2">Live Classes</div>
                    <div className="text-3xl font-black text-rose-500">{bundle?.live_classes?.length || 0}</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <div className="text-slate-500 text-sm font-bold mb-2">Mock Tests</div>
                    <div className="text-3xl font-black text-amber-500">{bundle?.exams?.length || 0}</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <div className="text-slate-500 text-sm font-bold mb-2">Books & Notes</div>
                    <div className="text-3xl font-black text-emerald-500">{bundle?.books?.length || 0}</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "live" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                {bundle?.live_classes?.length === 0 ? <p className="text-slate-500">No live classes scheduled.</p> : bundle?.live_classes?.map(lc => (
                  <div key={lc.id} className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                    <BadgeCheck size={32} className="text-rose-500 mb-4" />
                    <h3 className="font-black text-lg mb-2 text-slate-900 dark:text-white">{lc.title}</h3>
                    <p className="text-sm text-slate-500 mb-4">{new Date(lc.scheduled_at).toLocaleString()}</p>
                    <a href={lc.join_url} target="_blank" rel="noreferrer" className="block w-full py-2 bg-rose-500 hover:bg-rose-600 text-white text-center rounded-xl font-bold transition-colors">Join Class</a>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "exams" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                {bundle?.exams?.length === 0 ? <p className="text-slate-500">No exams available.</p> : bundle?.exams?.map(e => (
                  <div key={e.id} className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 flex justify-between items-center">
                    <div>
                      <h3 className="font-black text-lg text-slate-900 dark:text-white">{e.title}</h3>
                      <p className="text-sm text-slate-500">{e.duration_minutes} mins • {e.category}</p>
                    </div>
                    <button onClick={() => navigate(`/exam/${courseId}?examId=${e.id}`)} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold">Start Test</button>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "assignments" && (
              <div className="grid grid-cols-1 gap-4 animate-fade-in">
                {bundle?.assignments?.length === 0 ? <p className="text-slate-500">No assignments pending.</p> : bundle?.assignments?.map(a => (
                  <div key={a.id} className="bg-slate-50 dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 flex justify-between items-center">
                    <div>
                      <h3 className="font-black text-lg text-slate-900 dark:text-white">{a.title}</h3>
                      <p className="text-sm text-slate-500">Max Points: {a.max_points} • Deadline: {a.deadline ? new Date(a.deadline).toLocaleDateString() : "No deadline"}</p>
                    </div>
                    <button className="px-6 py-2 bg-white text-slate-700 hover:bg-slate-200 dark:bg-slate-900 dark:text-white rounded-xl font-bold">View Task</button>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "library" && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-fade-in">
                {bundle?.books?.length === 0 ? <p className="text-slate-500">Library is empty.</p> : bundle?.books?.map(b => (
                  <div key={b.id} className="group cursor-pointer" onClick={() => navigate(`/read-book/${b.id}`)}>
                    <div className="aspect-[3/4] bg-slate-200 dark:bg-slate-800 rounded-xl mb-3 overflow-hidden">
                      {b.thumbnail && <img src={b.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                    </div>
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white line-clamp-2">{b.title}</h3>
                    <p className="text-xs text-slate-500 mt-1">{b.category}</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "doubts" && (
              <div className="bg-slate-50 dark:bg-slate-800 p-8 rounded-3xl border border-slate-100 dark:border-slate-700 animate-fade-in text-center">
                <MessageSquare size={48} className="mx-auto text-indigo-500 mb-4 opacity-50" />
                <h3 className="text-xl font-black mb-2 text-slate-900 dark:text-white">Course Doubts Desk</h3>
                <p className="text-slate-500 mb-6">Ask your instructor questions directly related to this course.</p>
                <button onClick={() => navigate("/doubts")} className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 transition-colors">Go to Doubt Portal</button>
              </div>
            )}

            {activeTab === "ai-notes" && (
              <div className="bg-gradient-to-br from-purple-900 to-indigo-900 p-10 rounded-3xl text-white animate-fade-in relative overflow-hidden">
                <Brain size={120} className="absolute -right-10 -bottom-10 opacity-10" />
                <h3 className="text-3xl font-black mb-4">Course AI Tutor</h3>
                <p className="text-purple-200 mb-8 max-w-xl">Chat with an AI trained specifically on this course's curriculum. Generate summaries, take quizzes, and master concepts faster.</p>
                <button onClick={() => navigate("/ai-tutor")} className="px-8 py-4 bg-white text-indigo-900 rounded-xl font-black shadow-xl hover:scale-105 transition-transform flex items-center gap-2">
                  Launch AI Tutor <Sparkles size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Curriculum Sidebar */}
        <div className="w-80 lg:w-96 border-l dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex flex-col shrink-0 overflow-y-auto hidden md:flex">
          <div className="p-4 border-b dark:border-slate-800 font-bold flex justify-between items-center bg-white dark:bg-slate-900 sticky top-0 z-10">
            <span>Course Content</span>
            <span className="text-xs text-slate-500">{completedLessonIds.length}/{allLessons.length} Done</span>
          </div>
          <div className="flex-1">
            {curriculum.map((m, i) => (
              <div key={m.id}>
                <button onClick={() => toggleModule(m.id)} className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-800 text-left font-bold text-sm flex justify-between items-center sticky top-[53px] z-10 shadow-sm">
                  <span>{i + 1}. {m.title}</span>
                  {expandedModules[m.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedModules[m.id] && m.lessons?.map(l => (
                  <button key={l.id} onClick={() => setCurrentVideo(l)} className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between border-b dark:border-slate-800 ${currentVideo?.id === l.id ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 border-l-4 border-l-indigo-600" : "hover:bg-slate-50 dark:hover:bg-slate-800/50 border-l-4 border-l-transparent"}`}>
                    <div className="flex items-center gap-3">
                      {l.type === 'video' ? <Play size={14} className={currentVideo?.id === l.id ? "text-indigo-600" : "text-slate-400"} /> : <FileText size={14} className={currentVideo?.id === l.id ? "text-indigo-600" : "text-slate-400"} />}
                      <span className="truncate font-medium">{l.title}</span>
                    </div>
                    {completedLessonIds.includes(l.id) && <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
